import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, CheckCircle2, XCircle, FileText, Image as ImageIcon, Film, User } from "lucide-react";
import { toast } from "sonner";

const scoreColor = (n) => {
  if (n >= 80) return "text-emerald-600 border-emerald-200 bg-emerald-50";
  if (n >= 60) return "text-amber-600 border-amber-200 bg-amber-50";
  return "text-red-600 border-red-200 bg-red-50";
};

const attMime = (a) => (a.mime || a.content_type || a.type || "").toLowerCase();
const isImg = (a) => attMime(a).startsWith("image/") || /\.(png|jpe?g|gif|webp|heic)$/i.test(a.name || "");
const isVid = (a) => attMime(a).startsWith("video/") || /\.(mp4|mov|webm|m4v|avi)$/i.test(a.name || "");
const attUrl = (a) => (a.url ? (a.url.startsWith("http") ? a.url : `${API.replace(/\/api$/, "")}${a.url}`) : (a.id ? `${API}/files/${a.id}` : ""));

function IssueChip({ i }) {
  const color = { Valid: "bg-emerald-50 text-emerald-700 border-emerald-200", Duplicate: "bg-amber-50 text-amber-700 border-amber-200", Rejected: "bg-red-50 text-red-700 border-red-200", New: "bg-slate-50 text-slate-700 border-slate-200" }[i.status] || "bg-slate-50 text-slate-700 border-slate-200";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${color}`} title={i.title}>{i.status}</span>;
}

function SubmissionCard({ s, onDecided }) {
  const [action, setAction] = useState("accept");
  const [qs, setQs] = useState(s.submission_score);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = s.status === "Submitted";

  const submit = async () => {
    setBusy(true);
    try {
      const body = { action, notes };
      if (action === "accept") body.quality_score = Number(qs);
      await api.post(`/tasks/${s.task_id}/review`, body);
      toast.success(`${s.tester.name}: ${action === "accept" ? "Approved" : "Rejected"}`);
      onDecided();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`review-card-${s.task_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {s.tester.avatar_url ? (
            <img src={s.tester.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover"/>
          ) : (
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><User className="w-4 h-4"/></div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate" data-testid={`review-tester-${s.task_id}`}>{s.tester.name}</div>
            <div className="text-[11px] text-slate-500 truncate">
              {s.tester.tier ? <span className="mr-1.5 uppercase tracking-wide">{s.tester.tier}</span> : null}
              submitted {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : "—"}
            </div>
          </div>
        </div>
        <div className={`text-center border rounded-md px-3 py-1.5 shrink-0 ${scoreColor(s.submission_score)}`} data-testid={`review-score-${s.task_id}`}>
          <div className="text-[10px] uppercase tracking-wider">Score</div>
          <div className="font-mono text-lg leading-tight">{s.submission_score}</div>
        </div>
      </div>

      {s.is_low && s.insights.length > 0 && (
        <div className="mt-3 border border-red-200 bg-red-50 rounded-md p-3 flex gap-2" data-testid={`review-insights-${s.task_id}`}>
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5"/>
          <div className="text-xs text-red-900">
            <div className="font-semibold mb-1">Low score — review before deciding</div>
            <ul className="list-disc pl-4 space-y-0.5">
              {s.insights.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        </div>
      )}

      <div className="mt-3 grid md:grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-[10px] uppercase text-slate-500 mb-1">Submission notes</div>
          <div className="text-slate-800 whitespace-pre-wrap line-clamp-4">{s.submission_notes || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500 mb-1">Issues ({s.issues_stats.total})</div>
          <div className="flex flex-wrap gap-1">
            {s.issues.length === 0 && <span className="text-slate-500">None</span>}
            {s.issues.slice(0, 6).map(i => <IssueChip key={i.id} i={i}/>)}
            {s.issues.length > 6 && <span className="text-[10px] text-slate-500">+{s.issues.length - 6} more</span>}
          </div>
        </div>
      </div>

      {s.evidence_files?.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase text-slate-500 mb-1">Evidence</div>
          <div className="grid grid-cols-4 gap-2">
            {s.evidence_files.slice(0, 4).map((a, idx) => {
              const url = attUrl(a);
              return (
                <a key={idx} href={url} target="_blank" rel="noreferrer" className="border border-slate-200 rounded overflow-hidden hover:border-cyan-400" title={a.name}>
                  {isImg(a) && url ? <img src={url} alt="" className="w-full h-16 object-cover"/>
                    : isVid(a) && url ? <video src={url} className="w-full h-16 object-cover bg-slate-900" muted playsInline preload="metadata"/>
                    : <div className="h-16 flex items-center justify-center text-slate-400">{isImg(a)?<ImageIcon className="w-4 h-4"/>:isVid(a)?<Film className="w-4 h-4"/>:<FileText className="w-4 h-4"/>}</div>}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {pending ? (
        <div className="mt-3 border-t pt-3 space-y-2">
          <div className="flex gap-2">
            <Button size="sm" variant={action==="accept"?"default":"outline"} onClick={()=>setAction("accept")} className={action==="accept"?"bg-emerald-600 hover:bg-emerald-700":""} data-testid={`review-btn-accept-${s.task_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Approve</Button>
            <Button size="sm" variant={action==="reject"?"default":"outline"} onClick={()=>setAction("reject")} className={action==="reject"?"bg-red-600 hover:bg-red-700":""} data-testid={`review-btn-reject-${s.task_id}`}><XCircle className="w-3.5 h-3.5 mr-1"/>Reject</Button>
          </div>
          {action==="accept" && (
            <div className="flex items-center gap-2">
              <Label className="text-xs shrink-0">Quality score</Label>
              <Input type="number" min={0} max={100} value={qs} onChange={e=>setQs(e.target.value)} className="h-8 w-24 no-spinner" data-testid={`review-qs-${s.task_id}`}/>
              <span className="text-[10px] text-slate-500">(pre-filled from computed score)</span>
            </div>
          )}
          <Textarea rows={2} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Review notes (optional)" className="text-xs" data-testid={`review-notes-${s.task_id}`}/>
          <Button size="sm" onClick={submit} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid={`review-btn-confirm-${s.task_id}`}>
            {busy ? "Saving…" : `Confirm ${action}`}
          </Button>
        </div>
      ) : (
        <div className="mt-3 border-t pt-2 text-[11px] text-slate-500">
          {(s.status === "Assigned" || s.status === "In Progress")
            ? <>Not yet submitted · status <b>{s.status}</b></>
            : <>Already {s.status}{s.quality_score != null ? ` · quality ${s.quality_score}/100` : ""}</>}
          {s.review_notes ? <div className="mt-1 text-slate-600 italic">"{s.review_notes}"</div> : null}
        </div>
      )}
    </div>
  );
}

export default function ReviewAllSubmissionsDialog({ open, onOpenChange, taskId, onAnyDecided }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${taskId}/review-summary`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, taskId]);

  const handleDecided = async () => {
    await load();
    onAnyDecided?.();
  };

  const pending = data?.submissions?.filter(s => s.status === "Submitted") ?? [];
  const inProgress = data?.submissions?.filter(s => s.status === "Assigned" || s.status === "In Progress") ?? [];
  const decided = data?.submissions?.filter(s => !["Submitted", "Assigned", "In Progress"].includes(s.status)) ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="review-all-dialog">
        <DialogHeader>
          <DialogTitle>Review all submissions</DialogTitle>
          <DialogDescription>
            {data ? (
              <>
                <span className="font-medium text-slate-800">{data.task_group.title}</span>
                <span className="mx-1.5 text-slate-400">·</span>
                <span>{data.task_group.project_name}</span>
                <span className="mx-1.5 text-slate-400">·</span>
                <span>{data.submissions.length} submission{data.submissions.length === 1 ? "" : "s"}</span>
              </>
            ) : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-6 text-center text-sm text-slate-500">Loading submissions…</div>}
        {!loading && data && data.submissions.length === 0 && (
          <div className="py-6 text-center text-sm text-slate-500">No submissions found for this task group.</div>
        )}

        {!loading && pending.length > 0 && (
          <div>
            <div className="text-[11px] uppercase text-slate-500 mb-2 tracking-wider">Pending review ({pending.length})</div>
            <div className="space-y-3">
              {pending.map(s => <SubmissionCard key={s.task_id} s={s} onDecided={handleDecided}/>)}
            </div>
          </div>
        )}

        {!loading && inProgress.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase text-slate-500 mb-2 tracking-wider">Not yet submitted ({inProgress.length})</div>
            <div className="space-y-3">
              {inProgress.map(s => <SubmissionCard key={s.task_id} s={s} onDecided={handleDecided}/>)}
            </div>
          </div>
        )}

        {!loading && decided.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase text-slate-500 mb-2 tracking-wider">Already reviewed ({decided.length})</div>
            <div className="space-y-3">
              {decided.map(s => <SubmissionCard key={s.task_id} s={s} onDecided={handleDecided}/>)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
